import { useUserStore } from '@/stores/user';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { authRefreshPost } from '@brainvaulttech/dgr-react-sdk';
import { resetLogoutTimer } from '@/services/inactivityTimeOut';

const userStore = useUserStore.getState();

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = useUserStore.getState().accessToken;
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    resetLogoutTimer();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error?.response?.status === 401 &&
      originalRequest &&
      !originalRequest?._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      originalRequest._retry = true;
      try {
        const refreshTokenResponse = await authRefreshPost({ refreshToken: userStore.refreshToken! });
        const newAccessToken = refreshTokenResponse.data.tokens.accessToken;

        useUserStore.setState({ accessToken: newAccessToken });

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        resetLogoutTimer();
        return axios(originalRequest);
      } catch (refreshError) {
        userStore.reset();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const get = <T>(url: string, config?: AxiosRequestConfig) => api.get<T>(url, config);
const post = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.post<T>(url, data, config);
const put = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.put<T>(url, data, config);
const patch = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.patch<T>(url, data, config);
const del = <T>(url: string, config?: AxiosRequestConfig) => api.delete<T>(url, config);

export default {
  api,
  get,
  post,
  put,
  patch,
  delete: del,
};
